import styles from "./variablesManager.module.scss";
import { ModalWindow } from "../../shared/components/modalWindow/modalWindow";
import { useTranslation } from "react-i18next";
import { useGetAllVariablesQuery } from "../../api/variableService";
import {
  array,
  number,
  object
} from "yup";
import {
  useFieldArray,
  useForm,
  useWatch
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../store";
import {
  SelectedVariable,
  Variable
} from "../../types/variableType";
import { setSelectedVariables } from "../../store/slices/mainSlice";
import { SwitcherAddVariableModal } from "./switcherAddVariableModal/switcherAddVariableModal";
import { VariableItem } from "./variableItem/variableItem";

type VariablesManagerProps = {
  onClose: () => void;
}

export const VariablesManager = ({ onClose }: VariablesManagerProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);

  const [showAddVariableModal, setShowAddVariableModal] = useState<boolean>(false);

  const { data: variables, refetch: refetchVariables } = useGetAllVariablesQuery("");

  const schema = object({
    variables: array().required().of(
      object().shape({
        externalId: number().required(),
        valueId: number().required()
      })
    )
  });

  const defaultValues = useMemo(() => {
    return {
      variables: selectedVariables.map(variable => ({
        externalId: variable.id,
        valueId: variable.value.id
      })) || []
    }
  }, [selectedVariables]);

  const { control, setValue } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
    mode: "onChange"
  });
  const { fields: variablesFields, append: appendVariables } = useFieldArray({ control, name: "variables" });
  const variablesWatch = useWatch({control, name: "variables"});

  useEffect(() => {
    if (variablesWatch.length && !!variables) {
      const newSelectedVariables: Array<SelectedVariable> = [];
      variablesWatch.forEach((variableWatch) => {
        const findVariable = variables.find(item => item.id === variableWatch.externalId);
        if (findVariable) {
          const variableValue = findVariable.values.find(item => item.id === variableWatch.valueId);
          if (!!variableValue) {
            newSelectedVariables.push({
              id: findVariable.id,
              name: findVariable.name,
              value: variableValue
            });
          } else {
            newSelectedVariables.push({
              id: findVariable.id,
              name: findVariable.name,
              value: findVariable.values[0]
            })
          }
        }
      });
      dispatch(setSelectedVariables(newSelectedVariables));
    }
  }, [variablesWatch, variables, dispatch]);

  const onClickAddVariable = useCallback(() => {
    setShowAddVariableModal(true);
  }, [setShowAddVariableModal]);

  const updateFormValue = useCallback((variable: Variable, newVariableId: number) => {
    const index = variablesWatch.findIndex(item => item.externalId === variable.id);
    if (index > -1) {
      setValue(`variables.${index}.valueId`, newVariableId);
    }
  }, [variablesWatch, setValue]);

  const onCloseAddVariableModal = useCallback((result?: { variableId: number, variableValueId: number }) => {
    if (result) {
      refetchVariables();
      appendVariables({ externalId: result.variableId, valueId: result.variableValueId })
    }
    setShowAddVariableModal(false);
  }, [setShowAddVariableModal, refetchVariables, appendVariables]);

  return (
    <>
      <ModalWindow
        title={t("variableManager.title")}
        onClose={onClose}
        width={814}
        children={
          <div className={styles.container}>
            <div className={styles.mainContainer}>
              { variablesFields.map((variableField, index) => (
                <VariableItem control={control}
                              updateFormValue={updateFormValue}
                              key={variableField.id}
                              variableId={variableField.externalId}
                              name={`variables.${index}.valueId`} />
              ))}

            </div>
            <div className={styles.bottomContainer}>
              <div className={styles.buttons}>
                <button className={styles.close}
                        onClick={() => onClose()}>{t("common.close")}</button>
              </div>
              <div className={styles.buttons}>
                <button className={styles.add}
                        onClick={onClickAddVariable}>{t("variableManager.add")}</button>
              </div>
            </div>
          </div>
        }/>
      {showAddVariableModal && (
        <SwitcherAddVariableModal onClose={onCloseAddVariableModal}/>
      )}
    </>

  );
}